import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import { getPreJoinCapabilities } from '@modules/zoom/utils/capabilities';
import { detectDeviceType } from '@shared/utils/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import VideoMeetingLoading from '../VideoMeeting/VideoMeetingLoading';
import VideoMeetingDetails from './VideoMeetingDetails';
import VideoMeetingPreview from './VideoMeetingPreview';

type Props = {
  setIsWaitingRoom: (isWaitingRoom: boolean) => void;
};

const VideoWaitingRoom = (props: Props) => {
  const { setIsWaitingRoom } = props;

  const isMobile = detectDeviceType() === 'Mobile';
  const capabilities = useMemo(() => getPreJoinCapabilities(), []);
  const browserLimitations = capabilities.limitations;

  const [showWarning, setShowWarning] = useState<boolean>(
    isMobile || browserLimitations.length > 0
  );
  const { isMeetingLoading } = useContext(ZoomContext);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    params.delete('inMeeting');

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, []);

  if (isMeetingLoading) {
    return <VideoMeetingLoading />;
  }

  return (
    <section
      className="
        max-w-screen-largeLaptop m-auto flex flex-col gap-12
        tablet:p-14
        laptop:flex-row laptop:h-screen laptop:items-center laptop:gap-[clamp(4rem,7.2vw,6.5rem)]
        largeLaptop:pl-[4.875rem] largeLaptop:pr-[9.5rem]
      "
    >
      <VideoMeetingPreview />
      <VideoMeetingDetails setIsWaitingRoom={setIsWaitingRoom} />
      <Modal isOpen={showWarning} onClose={() => setShowWarning(false)} hasCloseIcon>
        <Modal.Header title="Before you join" />
        <Modal.Body className="flex flex-col justify-center items-center text-center">
          {isMobile ? (
            <p className="pb-10 text-white">
              Dear participant, please keep in mind that Screen Sharing will not be working on
              mobile and tablet devices. We strongly recommend using a laptop or desktop for the
              best experience.
            </p>
          ) : (
            <div className="pb-10 text-white flex flex-col gap-4">
              <p>You can join from this browser, but a few features are limited here:</p>
              <ul className="flex flex-col gap-2 text-left list-disc pl-6">
                {browserLimitations.map((limitation) => (
                  <li key={limitation.key}>{limitation.label}</li>
                ))}
              </ul>
              <p>
                For the full experience, we recommend Google Chrome or Microsoft Edge on a computer.
              </p>
            </div>
          )}
          <Button
            variant="primary"
            buttonText="Got it"
            className="h-8"
            onClick={() => setShowWarning(false)}
          />
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default VideoWaitingRoom;
